import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTareasStore } from "../store/api";
import { AutoDisablingButton } from "../elementos/AutoDisablingButton";
import { useCategoriaStore } from "../store/CategoriaStore";
import toast from "react-hot-toast";
import { faClone } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { validarFechaAvisoPersonalizado } from "../utils/validacionAviso";
import { globalApiError } from "../utils/generalApiError";
import { ApiErrorResponse } from "../DTO/ApiResponseError";
import axios from "axios";
import { Categoria } from "../DTO/Categoria";
import { TareaFormData } from "../DTO/Tarea";
import LoadingModal from "../elementos/LoadingModal";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";

interface FormularioTareaProps {
  id: string | null;
  tarea: TareaFormData | null;
}

const FormularioTarea: React.FC<FormularioTareaProps> = ({ id, tarea }) => {
  const [nombre, setNombre] = useState("");
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [descripcion, setDescripcion] = useState("");
  const [categoriaId, setCategoriaId] = useState("");
  const [categoriasDisponibles, setCategoriasDisponibles] = useState<Categoria[]>([]);
  const [fecha, setFecha] = useState(new Date().toISOString().split("T")[0]);
  const [hora, setHora] = useState("");
  const [completada, setCompletada] = useState(false);
  const [aviso, setAviso] = useState(false);
  const [fechaHoraAvisoPersonalizado, setFechaHoraAvisoPersonalizado] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const navigate = useNavigate();
  const { creatTarea } = useTareasStore();
  const { findAllCategorias } = useCategoriaStore();

  useEffect(() => {
    if (tarea) {
      setNombre(tarea?.nombre);
      setDescripcion(tarea.descripcion);
      setCategoriaId(String(tarea.categoriaId ?? ""));
      setFecha(tarea.fecha);
      setHora(tarea.hora);
      setCompletada(tarea.completada);
      setAviso(tarea.aviso);
      setFechaHoraAvisoPersonalizado(tarea.fechaHoraAvisoPersonalizado);
    }
  }, [tarea]);

  useEffect(() => {
    setDisabled(false);
    fetchAllCategorias();
  }, []);

  const fetchAllCategorias = async () => {
    try {
      const response = await findAllCategorias();
      setCategoriasDisponibles(response);
    } catch (error) {
      toast.error(globalApiError(error));
    }
  };

  const defaultObject = () => {
    setNombre("");
    setDescripcion("");
    setCategoriaId("");
    setFecha("");
    setHora("");
    setCompletada(false);
    setAviso(false);
    setFechaHoraAvisoPersonalizado(null);
    setFieldErrors({});
  };

  const validateForm = () => {
    return (
      nombre === "" ||
      descripcion === "" ||
      !categoriaId ||
      fecha === "" ||
      hora === ""
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFieldErrors({});
    if (!validateForm()) {
      try {
        const fechaActual = new Date().toISOString().split("T")[0];
        const esEdicion = tarea?.id !== null && tarea?.id !== undefined;
        const mantieneFechaOriginal = esEdicion && tarea?.fecha === fecha;

        if (fecha < fechaActual && !mantieneFechaOriginal) {
          toast.error("La fecha no puede ser anterior a la fecha actual");
          return;
        }
          const errorAviso = validarFechaAvisoPersonalizado(
            fechaHoraAvisoPersonalizado,
            tarea?.fechaHoraAvisoPersonalizado ?? null,
            fecha,
            hora,
          );

          if (errorAviso) {
            toast.error(errorAviso);
            return;
          }
          setDisabled(true);
          setLoading(true);
          await creatTarea(
            tarea?.id ?? null,
            nombre,
            descripcion,
            Number(categoriaId),
            fecha,
            hora,
            completada,
            aviso,
            fechaHoraAvisoPersonalizado,
          );
          navigate("/");
          toast.success("Tarea creada correctamente");
          defaultObject();
        
      } catch (error) {
        if (axios.isAxiosError<ApiErrorResponse>(error)) {
          const apiError = error.response?.data;
          const errors = apiError?.fieldErrors ?? {};

          if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
          }
        }

        toast.error(globalApiError(error));
      } finally {
        setDisabled(false);
        setLoading(false);
      }
    } else {
      toast.error("Debes rellenar todo el formulario antes de continuar");
    }
  };
  return (
    <div className="container mt-4">
      <LoadingModal show={loading} message={tarea ? "Guardando tarea..." : "Creando tarea..."} />
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <input type="hidden" value={tarea?.id ?? ""} />
          <label htmlFor="nombre" className="form-label">
            Nombre de la tarea
          </label>
          <input
            type="text"
            className="form-control"
            id="nombre"
            placeholder="Ej. Hacer la compra"
            required
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
          />
          {fieldErrors?.nombre && <div className="alert alert-danger py-2">{fieldErrors?.nombre}</div>}
        </div>

        <div className="mb-3 textarea-con-contador">
          <label htmlFor="descripcion" className="form-label">
            Descripción
          </label>
          <textarea
            className="form-control"
            id="descripcion"
            rows={3}
            maxLength={255}
            placeholder="Detalles adicionales de la tarea"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
          />

          <span className="contador-descripcion" aria-live="polite">
            {descripcion.length}/255
          </span>
        </div>
        {fieldErrors?.descripcion && (
          <div className="alert alert-danger py-2">
            {fieldErrors.descripcion}
          </div>
        )}

        <div className="row align-items-end g-3 mb-3">
          <div className="col-md-3">
            <label htmlFor="categoria" className="form-label">
              Categoria:
            </label>
            <select
              style={{
                width: window.innerWidth < 576 ? "300px" : "100%",
                fontSize: "1rem",
              }}
              id="completadaSelect"
              className="form-select"
              required
              value={categoriaId}
              onChange={(e) => {
                setCategoriaId(e.target.value);
              }}
            >
              <option value="">Selecciona una categoría</option>
              {categoriasDisponibles.map((cat) => (
                <option key={cat.id} value={String(cat.id)}>
                  {cat.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label htmlFor="fechaHoraAvisoPersonalizado" className="form-label">
              Aviso personalizado
            </label>
            <div className="campo-con-icono">
              <input
                className="form-control"
                type="datetime-local"
                value={fechaHoraAvisoPersonalizado || ""}
                onChange={(e) =>
                  setFechaHoraAvisoPersonalizado(e.target.value || null)
                }
                id="fechaHoraAvisoPersonalizado"
              />
              <button
                type="button"
                className="icono icono-movil"
                style={{ color: "#dc3545" }}
                onClick={() => setFechaHoraAvisoPersonalizado(null)}
                disabled={loading || !fechaHoraAvisoPersonalizado}
                aria-label="Borrar aviso personalizado"
                title="Borrar aviso personalizado"
              >
                <FontAwesomeIcon icon={faTrashCan} />
              </button>
            </div>
          </div>
          {tarea ? (
            <div className="col-auto pb-1">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={Boolean(tarea?.avisoPersonalizadoEnviado)}
                  disabled
                  id="avisoPersonalizadoEnviado"
                />
                <label
                  className="form-check-label"
                  htmlFor="avisoPersonalizadoEnviado"
                >
                  Enviado
                </label>
              </div>
            </div>
          ) : null}
        </div>

        <div className="mb-3">
          <label htmlFor="fecha" className="form-label">
            Fecha
          </label>
            <input
              type="date"
              className="form-control"
              id="fecha"
              required
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
          {fieldErrors?.fecha && <div className="alert alert-danger py-2">{fieldErrors.fecha}</div>}
        </div>

        <div className="mb-3">
          <label htmlFor="hora" className="form-label">
            Hora
          </label>
          <input
            type="time"
            className="form-control"
            id="hora"
            required
            value={hora}
            onChange={(e) => setHora(e.target.value)}
          />
          {fieldErrors?.hora && <div className="alert alert-danger py-2">{fieldErrors.hora}</div>}
        </div>

        <div className="mb-3">
          <div className="form-check">
            <label className="form-check-label">
              <input
                className="form-check-input"
                type="checkbox"
                checked={completada}
                onChange={(e) => setCompletada(e.target.checked)}
                id="completada"
              />
              Completada
            </label>
          </div>
        </div>

        <div className="mb-3">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              checked={aviso}
              onChange={(e) => setAviso(e.target.checked)}
              id="aviso"
            />

            <label className="form-check-label" htmlFor="aviso">
              Aviso
            </label>
          </div>
        </div>

        <AutoDisablingButton
          disabled={disabled}
          type="submit"
          className="btn btn-success me-2"
        >
          Guardar Tarea
        </AutoDisablingButton>

        <Link to={"/"} className="btn btn-primary  me-2">
          {" "}
          Volver atras
        </Link>
        {id ? (
          <Link
            to={"/duplicar/" + (tarea?.id || "")}
            className="btn btn-outline-secondary"
          >
            <FontAwesomeIcon icon={faClone} />
          </Link>
        ) : null}
      </form>
    </div>
  );
};

export default FormularioTarea;
